import type { CalculatedAttributesCollector } from '../../calculatedAttributes/collect';
import { resolveHighlightAttributeName } from '../ProductGridCard/highlightAttribute';

/**
 * Only what the collector reads. Declared here rather than imported: the grid's
 * mapper-typed component lives in the host app, and a collector that named it
 * would tie this package to that file.
 *
 * The product's attribute names are part of it because the card's reference may
 * carry a `uid` alone — see `resolveHighlightAttributeName`.
 */
type ProductCardGridForCollection = {
  id: string;
  items?: Array<{
    product?: {
      title?: string | null;
      attributes?: Array<{
        name?: { uid?: string | null; name?: string | null } | null;
      } | null> | null;
    } | null;
    highlightTextAttribute?: {
      uid?: string | null;
      name?: string | null;
    } | null;
  } | null>;
};

/**
 * The grid highlights one attribute per card, authored card by card, so it
 * reports one group per card rather than a single product list
 * (CMS-1890 / CMS-1786). The engine then keeps the cards whose attribute is
 * calculated in the page's vertical and drops the rest — the same answer the
 * grid used to compute itself, now made in one place.
 *
 * The attribute name comes from the same resolver the card's renderer uses, so
 * the two cannot disagree about which attribute a card shows: reporting the
 * authored `name` alone left a card referenced by `uid` out of the request
 * while the renderer still asked the engine for it, which blanked its metric
 * once a sibling card armed the calculation (CMS-1890 review).
 *
 * The grid has no mode, so it states no vertical and the page's stands in: a
 * grid follows the page it sits on — a Comparison Table or Selected Products if
 * one is there, the page's own terms otherwise.
 */
export const collectProductCardGridCalculatedAttributes: CalculatedAttributesCollector<
  ProductCardGridForCollection
> = (component) => ({
  groups: (component.items ?? []).map((item) => ({
    productIds: item?.product?.title ? [item.product.title] : [],
    attributeNames: [
      resolveHighlightAttributeName(
        item?.highlightTextAttribute,
        item?.product,
      ),
    ],
  })),
});
